import {
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILED,
  USER_SIGN_IN,
  USER_SIGN_IN_FAILED,
  USER_SIGN_OUT,
  CLEAR_LOGIN_ERROR,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILED,
  UPDATE_USER_WALLET_HISTORY,
  SEND_RESET_EMAIL,
  SEND_RESET_EMAIL_FAILED,
} from "../store/types";

import store from "../store/store";
import { firebase } from "../config/configureFirebase";
import { onValue, update, set, off, get } from "firebase/database";
import {
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithCustomToken,
} from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import base64 from "react-native-base64";
import AccessKey from "../other/AccessKey";

// Store references to cleanup listeners
let authStateUnsubscribe = null;
let userDataUnsubscribe = null;
let fetchUserRetryCount = 0;
const MAX_FETCH_USER_RETRIES = 10; // Maximum 10 retries (5 seconds total)
let pendingAppleProfile = null;

// Helper function for retry logic with exponential backoff
const retryFetch = async (url, options, maxRetries = 3, timeout = 10000) => {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      if (__DEV__) {
        console.log(
          `Fetch attempt ${attempt + 1} failed:`,
          error.message || error,
        );
      }

      if (attempt === maxRetries - 1) {
        throw error;
      }

      // Exponential backoff: wait 1s, 2s, 4s...
      const delay = Math.pow(2, attempt) * 1000;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
};

export const fetchUser = () => (dispatch) => {
  const { auth, config, singleUserRef } = firebase;

  // Validate that Firebase auth and required refs are initialized
  if (!auth || !singleUserRef) {
    fetchUserRetryCount++;

    if (fetchUserRetryCount > MAX_FETCH_USER_RETRIES) {
      if (__DEV__) {
        console.log(
          "Max retries reached for fetchUser. Firebase may not be properly initialized.",
        );
      }
      // Reset counter and dispatch error
      fetchUserRetryCount = 0;
      dispatch({
        type: FETCH_USER_FAILED,
        payload: {
          code: store.getState().languagedata.defaultLanguage.auth_error,
          message: "Firebase initialization timeout. Please restart the app.",
        },
      });
      return;
    }

    if (__DEV__) {
      console.log(
        `Firebase not fully initialized yet (auth: ${!!auth}, singleUserRef: ${!!singleUserRef}), retry ${fetchUserRetryCount}/${MAX_FETCH_USER_RETRIES} in 500ms...`,
      );
    }
    // Retry after a short delay to allow Firebase to initialize
    setTimeout(() => {
      dispatch(fetchUser());
    }, 500);
    return;
  }

  // Reset retry counter on success
  fetchUserRetryCount = 0;

  // Clean up previous listeners if they exist
  if (authStateUnsubscribe) {
    authStateUnsubscribe();
    authStateUnsubscribe = null;
  }

  if (userDataUnsubscribe) {
    userDataUnsubscribe();
    userDataUnsubscribe = null;
  }

  dispatch({
    type: FETCH_USER,
    payload: null,
  });

  try {
    authStateUnsubscribe = onAuthStateChanged(auth, (user) => {
      // Clean up previous userData listener before creating a new one
      if (userDataUnsubscribe) {
        userDataUnsubscribe();
        userDataUnsubscribe = null;
      }

      if (user) {
        try {
          userDataUnsubscribe = onValue(
            singleUserRef(user.uid),
            async (snapshot) => {
              try {
                if (snapshot.val()) {
                  let profile = snapshot.val();
                  profile.uid = user.uid;
                  // Asegurar usertype por compatibilidad con perfiles antiguos
                  if (!profile.usertype) {
                    try {
                      await update(singleUserRef(user.uid), {
                        usertype: "customer",
                      });
                      profile.usertype = "customer";
                    } catch (e) {
                      // si falla la actualización, continuamos sin forzar logout
                    }
                  }
                  dispatch({
                    type: FETCH_USER_SUCCESS,
                    payload: profile,
                  });
                } else {
                  let data = {
                    uid: user.uid,
                    mobile: "",
                    email: "",
                    firstName: "",
                    lastName: "",
                    verifyId: "",
                  };

                  if (user.providerData.length == 0 && user.email) {
                    data.email = user.email;
                  }
                  if (user.providerData.length > 0 && user.phoneNumber) {
                    data.mobile = user.phoneNumber;
                  }
                  if (user.providerData.length > 0) {
                    const provideData = user.providerData[0];
                    if (provideData.providerId === "phone") {
                      data.mobile = provideData.phoneNumber;
                    }
                    if (
                      provideData.providerId == "google.com" ||
                      provideData.providerId == "apple.com"
                    ) {
                      if (provideData.email) {
                        data.email = provideData.email;
                      }
                      if (provideData.phoneNumber) {
                        data.mobile = provideData.phoneNumber;
                      }
                      if (provideData.displayName) {
                        const name = String(
                          provideData.displayName || "",
                        ).trim();
                        const parts = name.split(" ").filter(Boolean);
                        data.firstName = parts[0] || name || "";
                        data.lastName = parts[1] || "";
                      }
                      if (provideData.photoURL) {
                        data["profile_image"] = provideData.photoURL;
                      }
                      if (
                        provideData.providerId === "apple.com" &&
                        pendingAppleProfile
                      ) {
                        if (pendingAppleProfile.email) {
                          data.email = pendingAppleProfile.email;
                        }
                        if (pendingAppleProfile.firstName) {
                          data.firstName = pendingAppleProfile.firstName;
                        }
                        if (pendingAppleProfile.lastName) {
                          data.lastName = pendingAppleProfile.lastName;
                        }
                        pendingAppleProfile = null;
                      }
                    }
                  }
                  if (user.providerData.length > 0 && user.verifyId) {
                    data.verifyId = user.verifyId;
                  }

                  const settings = store.getState().settingsdata.settings;
                  const isWeb =
                    typeof window !== "undefined" && window.location;
                  const host =
                    isWeb &&
                    settings &&
                    settings.CompanyWebsite === window.location.origin
                      ? window.location.origin
                      : `https://${config.projectId}.web.app`;
                  let url = `https://us-central1-urbaapp.cloudfunctions.net/check_auth_exists`;
                  try {
                    const json = await retryFetch(
                      url,
                      {
                        method: "POST",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization:
                            "Basic " +
                            base64.encode(config.projectId + ":" + AccessKey),
                        },
                        body: JSON.stringify({ data: JSON.stringify(data) }),
                      },
                      3,
                      10000,
                    );

                    if (json.uid) {
                      dispatch({
                        type: FETCH_USER_SUCCESS,
                        payload: json,
                      });
                    } else {
                      dispatch({
                        type: FETCH_USER_FAILED,
                        payload: {
                          code: store.getState().languagedata.defaultLanguage
                            .auth_error,
                          message: json.error,
                        },
                      });
                    }
                  } catch (error) {
                    if (__DEV__) {
                      console.log(
                        "Error fetching user from cloud function:",
                        error.message || error,
                      );
                    }
                    // Don't logout on network errors, keep current session if exists
                    const currentProfile = store.getState().auth.profile;
                    if (!currentProfile) {
                      // Only logout if we don't have a current profile
                      dispatch({
                        type: FETCH_USER_FAILED,
                        payload: {
                          code: store.getState().languagedata.defaultLanguage
                            .auth_error,
                          message:
                            "Network error. Please check your connection.",
                        },
                      });
                    }
                  }
                }
              } catch (error) {
                if (__DEV__) {
                  console.log(
                    "Error in onValue callback:",
                    error.message || error,
                  );
                }
                // Don't logout on errors, keep current session if exists
                const currentProfile = store.getState().auth.profile;
                if (!currentProfile) {
                  dispatch({
                    type: FETCH_USER_FAILED,
                    payload: {
                      code: store.getState().languagedata.defaultLanguage
                        .auth_error,
                      message: "Error loading user data.",
                    },
                  });
                }
              }
            },
            (error) => {
              // Error callback for onValue
              if (__DEV__) {
                console.log("onValue error:", error.message || error);
              }
              // Don't logout on database errors, keep current session if exists
              const currentProfile = store.getState().auth.profile;
              if (!currentProfile) {
                dispatch({
                  type: FETCH_USER_FAILED,
                  payload: {
                    code: store.getState().languagedata.defaultLanguage
                      .auth_error,
                    message: "Database connection error.",
                  },
                });
              }
            },
          );
        } catch (error) {
          if (__DEV__) {
            console.log(
              "Error setting up userData listener:",
              error.message || error,
            );
          }
          // Don't logout on setup errors
          const currentProfile = store.getState().auth.profile;
          if (!currentProfile) {
            dispatch({
              type: FETCH_USER_FAILED,
              payload: {
                code: store.getState().languagedata.defaultLanguage.auth_error,
                message: "Error setting up user listener.",
              },
            });
          }
        }
      } else {
        // When user is null, wait a bit to check if it's a temporary token refresh
        // Firebase Auth sometimes briefly sets user to null during token refresh
        // Also, after reload, Firebase Auth needs time to restore session from AsyncStorage
        const currentProfile = store.getState().auth.profile;
        const delay = currentProfile ? 5000 : 2000; // Wait longer if user was logged in (reload scenario)

        setTimeout(() => {
          // Validate auth exists before checking currentUser
          if (!auth) {
            if (__DEV__) {
              console.log("Auth not available, cannot check currentUser");
            }
            // If we had a profile before, don't logout - Firebase may still be initializing
            if (currentProfile) {
              if (__DEV__) {
                console.log(
                  "Had profile before, keeping session while Firebase initializes",
                );
              }
              return;
            }
            return;
          }
          // Check again if user is still null after delay
          if (!auth.currentUser) {
            // If we had a profile before, wait a bit more - session might still be restoring
            if (currentProfile) {
              if (__DEV__) {
                console.log(
                  "User was logged in but auth.currentUser is null, waiting 3 more seconds...",
                );
              }
              setTimeout(() => {
                if (!auth || !auth.currentUser) {
                  if (__DEV__) {
                    console.log(
                      "User still null after extended delay, dispatching FETCH_USER_FAILED",
                    );
                  }
                  dispatch({
                    type: FETCH_USER_FAILED,
                    payload: {
                      code: store.getState().languagedata.defaultLanguage
                        .auth_error,
                      message:
                        store.getState().languagedata.defaultLanguage
                          .not_logged_in,
                    },
                  });
                } else {
                  if (__DEV__) {
                    console.log(
                      "User restored after extended delay - session restored from AsyncStorage",
                    );
                  }
                }
              }, 3000);
            } else {
              if (__DEV__) {
                console.log(
                  "User is null after delay, dispatching FETCH_USER_FAILED",
                );
              }
              dispatch({
                type: FETCH_USER_FAILED,
                payload: {
                  code: store.getState().languagedata.defaultLanguage
                    .auth_error,
                  message:
                    store.getState().languagedata.defaultLanguage.not_logged_in,
                },
              });
            }
          } else {
            if (__DEV__) {
              console.log(
                "User restored after delay, likely token refresh or session restored - not logging out",
              );
            }
          }
        }, delay);
      }
    });
  } catch (error) {
    if (__DEV__) {
      console.log(
        "Error setting up onAuthStateChanged:",
        error.message || error,
      );
    }
    // If there's an error, retry after a delay
    setTimeout(() => {
      dispatch(fetchUser());
    }, 1000);
  }
};

export const validateReferer = async (referralId) => {
  const { config } = firebase;
  const response = await fetch(
    `https://us-central1-urbaapp.cloudfunctions.net/validate_referrer`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        referralId: referralId,
      }),
    },
  );
  const json = await response.json();
  return json;
};

export const checkUserExists = async (data) => {
  const { config } = firebase;

  const settings = store.getState().settingsdata.settings;
  const isWeb = typeof window !== "undefined" && window.location;
  let host =
    isWeb && settings && settings.CompanyWebsite === window.location.origin
      ? window.location.origin
      : `https://${config.projectId}.web.app` || `http://localhost:3000`;
  let url = `https://us-central1-urbaapp.cloudfunctions.net/check_user_exists`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization:
        "Basic " + base64.encode(config.projectId + ":" + AccessKey),
    },
    body: JSON.stringify({
      email: data.email,
      mobile: data.mobile,
    }),
  });
  const json = await response.json();
  return json;
};

export const mainSignUp = async (regData) => {
  const { config } = firebase;
  let url = `https://us-central1-urbaapp.cloudfunctions.net/user_signup`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ regData: regData }),
  });
  const res = await response.json();
  return res;
};

export const updateProfileWithEmail = (profileData) => async (dispatch) => {
  const { config } = firebase;
  try {
    const settings = store.getState().settingsdata.settings;
    let host =
      window &&
      window.location &&
      settings.CompanyWebsite === window.location.origin
        ? window.location.origin
        : `https://${config.projectId}.web.app`;
    let url = `https://us-central1-urbaapp.cloudfunctions.net/update_user_email`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:
          "Basic " + base64.encode(config.projectId + ":" + AccessKey),
      },
      body: JSON.stringify(profileData),
    });
    const result = await response.json();
    if (result.error) {
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error: error };
  }
};

export const requestPhoneOtpDevice = (verificationId) => async (dispatch) => {
  dispatch({
    type: REQUEST_OTP_SUCCESS,
    payload: verificationId,
  });
};

export const mobileSignIn = (verficationId, code) => (dispatch) => {
  const { auth, mobileAuthCredential } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null,
  });
  signInWithCredential(auth, mobileAuthCredential(verficationId, code))
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error,
      });
    });
};

export const saveAddresses = async (uid, location, name) => {
  const { singleUserRef } = firebase;
  onValue(
    child(singleUserRef(uid), "savedAddresses"),
    (savedAdd) => {
      if (savedAdd.val()) {
        let addresses = savedAdd.val();
        let didNotMatch = true;
        for (let key in addresses) {
          let entry = addresses[key];
          if (entry.name == name) {
            didNotMatch = false;
            update(child(singleUserRef(uid), "savedAddresses/" + key), {
              description: location.add,
              lat: location.lat,
              lng: location.lng,
              count: 1,
              name: name,
            });
            break;
          }
        }
        if (didNotMatch) {
          push(child(singleUserRef(uid), "savedAddresses"), {
            description: location.add,
            lat: location.lat,
            lng: location.lng,
            count: 1,
            name: name,
          });
        }
      } else {
        push(child(singleUserRef(uid), "savedAddresses"), {
          description: location.add,
          lat: location.lat,
          lng: location.lng,
          count: 1,
          name: name,
        });
      }
    },
    { onlyOnce: true },
  );
};

export const googleLogin = (idToken, accessToken) => (dispatch) => {
  const { auth, googleCredential } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null,
  });

  const credential = googleCredential(idToken, accessToken);
  signInWithCredential(auth, credential)
    .then((user) => {
      //OnAuthStateChange takes care of Navigation
    })
    .catch((error) => {
      console.log(error);
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error,
      });
    });
};

export const appleSignIn = (credentialData) => (dispatch) => {
  const { auth, appleProvider } = firebase;

  dispatch({
    type: USER_SIGN_IN,
    payload: null,
  });
  if (credentialData) {
    if (
      credentialData.email ||
      credentialData.firstName ||
      credentialData.lastName
    ) {
      pendingAppleProfile = {
        email: credentialData.email || "",
        firstName: credentialData.firstName || "",
        lastName: credentialData.lastName || "",
      };
    }
    const credential = appleProvider.credential({
      idToken: credentialData.idToken,
      rawNonce: credentialData.rawNonce,
    });
    signInWithCredential(auth, credential)
      .then((user) => {
        //OnAuthStateChange takes care of Navigation
      })
      .catch((error) => {
        pendingAppleProfile = null;
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error,
        });
      });
  } else {
    signInWithPopup(auth, appleProvider)
      .then(function (result) {
        signInWithCredential(auth, result.credential)
          .then((user) => {
            //OnAuthStateChange takes care of Navigation
          })
          .catch((error) => {
            dispatch({
              type: USER_SIGN_IN_FAILED,
              payload: error,
            });
          });
      })
      .catch(function (error) {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error,
        });
      });
  }
};

export const signOff = () => (dispatch) => {
  const { auth, singleUserRef, walletHistoryRef, userNotificationsRef } =
    firebase;

  const uid = auth.currentUser.uid;

  off(singleUserRef(uid));
  off(walletHistoryRef(uid));
  off(userNotificationsRef(uid));

  onValue(
    singleUserRef(uid),
    (snapshot) => {
      if (snapshot.val()) {
        const profile = snapshot.val();
        if (profile && profile.usertype === "driver") {
          update(singleUserRef(uid), { driverActiveStatus: false });
        }
        setTimeout(() => {
          signOut(auth)
            .then(() => {
              dispatch({
                type: USER_SIGN_OUT,
                payload: null,
              });
            })
            .catch((error) => {});
        }, 2000);
      }
    },
    { onlyOnce: true },
  );
};

export const updateProfile = (updateData) => async (dispatch) => {
  const {
    auth,
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef,
    vehicleInspectionRef,
  } = firebase;

  const uid = auth.currentUser.uid;
  if (updateData.licenseImage) {
    await uploadBytesResumable(driverDocsRef(uid), updateData.licenseImage);
    updateData.licenseImage = await getDownloadURL(driverDocsRef(uid));
  }
  if (updateData.licenseImageBack) {
    await uploadBytesResumable(
      driverDocsRefBack(uid),
      updateData.licenseImageBack,
    );
    updateData.licenseImageBack = await getDownloadURL(driverDocsRefBack(uid));
  }
  if (updateData.verifyIdImage) {
    await uploadBytesResumable(verifyIdImageRef(uid), updateData.verifyIdImage);
    updateData.verifyIdImage = await getDownloadURL(verifyIdImageRef(uid));
  }
  if (updateData.vehicleInspectionImage) {
    await uploadBytesResumable(
      vehicleInspectionRef(uid),
      updateData.vehicleInspectionImage,
    );
    updateData.vehicleInspectionImage = await getDownloadURL(
      vehicleInspectionRef(uid),
    );
  }

  await update(singleUserRef(uid), updateData);
};

export const updateProfileImage = (imageBlob) => {
  const { auth, singleUserRef, profileImageRef } = firebase;

  const uid = auth.currentUser.uid;

  uploadBytesResumable(profileImageRef(uid), imageBlob)
    .then(() => {
      imageBlob.close();
      return getDownloadURL(profileImageRef(uid));
    })
    .then((url) => {
      update(singleUserRef(uid), {
        profile_image: url,
      });
    });
};

export const updateWebProfileImage = async (imageBlob) => {
  const { auth, singleUserRef, profileImageRef } = firebase;

  const uid = auth.currentUser.uid;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), { profile_image: image });
};
export const updateCustomerProfileImage = async (imageBlob, id) => {
  const { singleUserRef, profileImageRef } = firebase;

  const uid = id;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), { profile_image: image });
};

export const updatePushToken = (token, platform) => {
  const { auth, singleUserRef } = firebase;

  const uid = auth.currentUser.uid;

  update(singleUserRef(uid), {
    pushToken: token,
    userPlatform: platform,
  });
};

export const clearLoginError = () => (dispatch) => {
  dispatch({
    type: CLEAR_LOGIN_ERROR,
    payload: null,
  });
};

const enrichWalletHistoryWithServiceFee = async (history, singleBookingRef) => {
  if (!history || history.length === 0 || !singleBookingRef) {
    return history || [];
  }

  const enriched = [...history];
  const bookingFeeCache = {};
  const maxItemsToEnrich = 30;

  for (
    let index = 0;
    index < enriched.length && index < maxItemsToEnrich;
    index++
  ) {
    const item = enriched[index];
    if (!item || item.type !== "Credit") {
      continue;
    }

    const hasServiceFee =
      item.serviceFee !== undefined && item.serviceFee !== null;
    if (hasServiceFee) {
      continue;
    }

    const txRef = item.txRef ? String(item.txRef) : "";
    if (!txRef || txRef.startsWith("wallet")) {
      continue;
    }

    if (bookingFeeCache[txRef] === undefined) {
      try {
        const bookingSnapshot = await get(singleBookingRef(txRef));
        const booking = bookingSnapshot.val();
        const fee = parseFloat(booking?.convenience_fees);
        bookingFeeCache[txRef] = Number.isFinite(fee) ? fee : 0;
      } catch (e) {
        bookingFeeCache[txRef] = 0;
      }
    }

    const serviceFee = bookingFeeCache[txRef];
    if (serviceFee > 0) {
      enriched[index] = {
        ...item,
        serviceFee,
      };
    }
  }

  return enriched;
};

export const fetchWalletHistory = () => (dispatch) => {
  const { auth, walletHistoryRef, singleBookingRef } = firebase;

  const uid = auth.currentUser.uid;

  onValue(walletHistoryRef(uid), async (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      const walletHistory = await enrichWalletHistoryWithServiceFee(
        arr.reverse(),
        singleBookingRef
      );
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: walletHistory,
      });
    } else {
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: [],
      });
    }
  });
};
export const fetchUserWalletHistory = (userId) => (dispatch) => {
  const { auth, walletHistoryRef, singleBookingRef } = firebase;

  const uid = userId;

  onValue(walletHistoryRef(uid), async (snapshot) => {
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map((i) => {
        data[i].id = i;
        return data[i];
      });
      const walletHistory = await enrichWalletHistoryWithServiceFee(
        arr.reverse(),
        singleBookingRef
      );
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: walletHistory,
      });
    } else {
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: [],
      });
    }
  });
};

export const sendResetMail = (email) => async (dispatch) => {
  const { authRef } = firebase;

  dispatch({
    type: SEND_RESET_EMAIL,
    payload: email,
  });
  sendPasswordResetEmail(authRef(), email)
    .then(function () {
      console.log("Email send successfuly");
    })
    .catch(function (error) {
      dispatch({
        type: SEND_RESET_EMAIL_FAILED,
        payload: {
          code: store.getState().languagedata.defaultLanguage.auth_error,
          message: store.getState().languagedata.defaultLanguage.not_registred,
        },
      });
    });
};

export const verifyEmailPassword = (email, pass) => async (dispatch) => {
  const { authRef, singleUserRef } = firebase;

  signInWithEmailAndPassword(authRef(), email, pass)
    .then((userCredential) => {
      const user = userCredential.user;
      onValue(singleUserRef(user.uid), (snapshot) => {
        if (snapshot.val()) {
          const profile = snapshot.val();
          const franchiseId = profile.franchise_id; // Asegúrate de que el perfil del usuario tenga este campo
          if (franchiseId) {
            localStorage.setItem("franchise_id", franchiseId);
          }
        }
      });
    })
    .catch((error) => {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error,
      });
    });
};

export const requestMobileOtp = (mobile) => async (dispatch) => {
  const { config } = firebase;
  dispatch({
    type: REQUEST_OTP,
    payload: true,
  });

  const settings = store.getState().settingsdata.settings;
  const isWeb = typeof window !== "undefined" && window.location;
  let host =
    isWeb && settings && settings.CompanyWebsite === window.location.origin
      ? window.location.origin
      : `https://${config.projectId}.web.app`;
  let url = `https://us-central1-urbaapp.cloudfunctions.net/request_mobile_otp`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ mobile: mobile }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    if (result.success) {
      // For custom OTP, we use 'true' as verificationId to indicate OTP was sent
      // The actual verification happens in verifyMobileOtp
      dispatch({
        type: REQUEST_OTP_SUCCESS,
        payload: true,
      });
    } else {
      dispatch({
        type: REQUEST_OTP_FAILED,
        payload: result.error || "Failed to send OTP",
      });
    }
  } catch (error) {
    console.log("Error requesting mobile OTP:", error);
    dispatch({
      type: REQUEST_OTP_FAILED,
      payload: error.message || "Network error. Please check your connection.",
    });
  }
};

/** Evita dos llamadas paralelas a verify_mobile_otp (Twilio solo acepta el código una vez). */
let verifyMobileOtpInFlight = false;

export const verifyMobileOtp = (mobile, otp) => async (dispatch) => {
  // Verificar que los parámetros sean válidos
  if (!mobile || !otp) {
    console.error("verifyMobileOtp: Parámetros inválidos");
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: { message: "Número de teléfono o código OTP faltante" },
    });
    return;
  }

  if (verifyMobileOtpInFlight) {
    if (__DEV__) {
      console.log("verifyMobileOtp: ignorando llamada duplicada en curso");
    }
    return;
  }

  const { auth, config } = firebase;

  if (!auth) {
    console.error("verifyMobileOtp: Firebase Auth no está inicializado");
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: { message: "Error de configuración de Firebase" },
    });
    return;
  }

  verifyMobileOtpInFlight = true;
  const body = {
    mobile: mobile,
    otp: otp,
  };
  try {
    const settings = store.getState().settingsdata.settings;
    const isWeb = typeof window !== "undefined" && window.location;
    let host =
      isWeb && settings && settings.CompanyWebsite === window.location.origin
        ? window.location.origin
        : `https://${config.projectId}.web.app`;
    let url = `https://us-central1-urbaapp.cloudfunctions.net/verify_mobile_otp`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("verifyMobileOtp: Error HTTP:", response.status, errorText);
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: { message: `Error del servidor: ${response.status}` },
      });
      return;
    }

    const result = await response.json();

    if (result.token) {
      // Validar que el token no esté vacío y tenga el formato correcto
      const token = result.token.trim();
      if (!token || token.length === 0) {
        console.error("verifyMobileOtp: Token inválido recibido (vacío)");
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: { message: "Token de autenticación inválido" },
        });
        return;
      }

      // Validar formato básico del JWT (debe tener 3 partes separadas por puntos)
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) {
        console.error("verifyMobileOtp: Token no tiene formato JWT válido");
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: { message: "Token de autenticación con formato inválido" },
        });
        return;
      }

      try {
        const userCredential = await signInWithCustomToken(auth, token);
        // OnAuthStateChange takes care of Navigation
      } catch (error) {
        console.error(
          "verifyMobileOtp: Error en signInWithCustomToken:",
          error.code,
          error.message,
        );

        // Proporcionar mensaje de error más amigable
        let errorMessage = error.message || "Error al autenticar";
        if (error.code === "auth/invalid-value-at-'session-info'") {
          errorMessage =
            "Error de autenticación. Por favor, intenta nuevamente o contacta al soporte.";
        }

        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: { ...error, message: errorMessage },
        });
      }
    } else {
      // Mensaje más amigable para errores comunes
      let errorMessage = result.error || "No se recibió token de autenticación";
      if (result.error && result.error.includes("no encontrado o expirado")) {
        errorMessage =
          "El código OTP ya fue usado o ha expirado. Por favor, solicita un nuevo código.";
      }

      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: errorMessage,
      });
    }
  } catch (error) {
    console.error("verifyMobileOtp: Error:", error);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: error,
    });
  } finally {
    verifyMobileOtpInFlight = false;
  }
};

export const updateAuthMobile = async (mobile, otp) => {
  const { auth, config } = firebase;

  const uid = auth.currentUser.uid;
  const body = {
    uid: uid,
    mobile: mobile,
    otp: otp,
  };

  const settings = store.getState().settingsdata.settings;
  const isWeb = typeof window !== "undefined" && window.location;
  let host =
    isWeb && settings && settings.CompanyWebsite === window.location.origin
      ? window.location.origin
      : `https://${config.projectId}.web.app`;
  let url = `https://us-central1-urbaapp.cloudfunctions.net/update_auth_mobile`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    const result = await response.json();
    if (result.success) {
      return { success: true };
    } else {
      return { success: false, error: result.error };
    }
  } catch (error) {
    return { success: false, error: error };
  }
};
